import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceEffect, useInViewport } from 'ahooks';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { IconLoadindLine } from 'luckybuy-react-icon';

import PageLayout from '@/components/PageLayout';
import iconBtc from '@/assets/icons/blockchain/btc.png';
import CardA from '@/components/Launch/Card/CardA';
import emptyIcon from 'assets/icons/myraffles/empty.png';
import {
  CollectionList,
  CollectionListData,
  getCollectionList,
} from 'api/launchpad/nft_launchpad_collection_list';
import Help2 from '@/components/Launch/Help2';
import Help from '@/components/Launch/Help';
import LuckyMintCongratulation from '@/components/Launch/LuckyMintCongratulation';
import LaunchWithLuckyGo from '@/components/NavBar/LaunchWithLuckyGo';
import LaunchWithLucky from '@/components/Launch/LaunchWithLucky';
import { getLotRecentlyRoundWithWinner } from 'api/luckymint/lot_recently_round_with_winner';
import { useUserInfo } from 'lib/user';
import { event } from 'api/event';

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const collectionListData: CollectionListData = await getCollectionList(1);
  const recentlyRoundWithWinner = await getLotRecentlyRoundWithWinner(
    ctx.req?.cookies?.Authorization as string
  );
  const x_forwarded_for = ctx.req.headers['x-original-forwarded-for'];
  if (x_forwarded_for) {
    try {
      const ipReq = await fetch(
        `http://geo-service.infra.svc.cluster.local/api/v1/check/mainland-ip?ip=${x_forwarded_for}`
      );
      const ipRes = await ipReq.json();
      if (ipRes?.data?.result) {
        return {
          redirect: {
            destination: '/403',
            permanent: true,
          },
        };
      }
    } catch (error) {}
  }
  return {
    props: {
      collectionListData: collectionListData || {},
      recentlyRoundWithWinner: recentlyRoundWithWinner || {},
    },
  };
}

const Launchpad = ({
  collectionListData,
  recentlyRoundWithWinner,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { address } = useUserInfo();
  const inViewportRef = useRef(null);
  const [inViewport] = useInViewport(inViewportRef);
  const [currentPage, setCurrentPage] = useState(collectionListData?.page || 0);
  const [hasNext, setHasNext] = useState(collectionListData?.has_next || false);
  const [list, setList] = useState<CollectionList[]>(
    collectionListData?.list || []
  );
  const [scrollLoading, setScrollLoading] = useState(false);

  useEffect(() => {
    event({
      event: 'launchpad_list_page_view',
      biz: 'Luckyplus',
      page: 'launchpad',
      address: address,
      uid: localStorage.getItem('uuid'),
    });
  }, [address]);

  const getList = useCallback(async () => {
    setScrollLoading(true);
    const res = await getCollectionList(currentPage + 1);
    setScrollLoading(false);
    setCurrentPage(res?.page);
    setHasNext(res?.has_next);
    setList(list?.concat(res?.list));
  }, [currentPage, list]);

  useDebounceEffect(
    () => {
      if (inViewport && !scrollLoading && hasNext) {
        getList();
      }
    },
    [inViewport, scrollLoading, hasNext],
    {
      wait: 200,
    }
  );

  const listRender = useCallback(() => {
    if (!list || list?.length === 0) {
      return (
        <div className="flex items-center justify-center gap-2 py-[120px]">
          <img src={emptyIcon.src} className="w-6" alt="" />
          <div className="text-base font-normal text-[#FCFCFD]">
            It looks kinda empty here
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-6 lg:grid-cols-4 2xl:grid-cols-6 3xl:grid-cols-7">
            {list?.map((item) => (
              <CardA
                key={item?.vanity_url}
                logo={item?.logo}
                isFreeMint={item?.is_free_mint}
                name={item?.name}
                state={item?.state}
                startTime={item?.start_time}
                vanityUrl={item?.vanity_url}
                mintPrice={Number(item?.mint_price)}
                supply={item?.supply}
                available={item?.available}
              />
            ))}
          </div>
          <div className="h-[0.1px]" ref={inViewportRef}></div>
          {scrollLoading && (
            <div className="flex items-center justify-center gap-2 py-5">
              <IconLoadindLine size={48} className="animate-spin text-white" />
            </div>
          )}
        </div>
      );
    }
  }, [list, scrollLoading]);

  return (
    <PageLayout btnList={<LaunchWithLuckyGo />} full={true}>
      <div className="flex min-h-[calc(100vh-146px)] flex-col justify-between gap-[60px]">
        <div>
          <LuckyMintCongratulation
            roundId={recentlyRoundWithWinner?.id}
            allocatedDollarAmount={
              recentlyRoundWithWinner?.allocated_dollar_amount
            }
          />
          <div className="mb-8 mt-[60px] text-[32px] font-bold leading-[40px] text-white md:flex md:items-center">
            <img
              src={iconBtc.src}
              className="mb-[6px] mr-4 inline w-8 shrink-0 md:mb-0"
              alt=""
            />
            <span>Ordinals Collections Launchpad</span>
          </div>
          <div>{listRender()}</div>
        </div>
        <div className="flex flex-col gap-8">
          <LaunchWithLucky />
          <Help2 />
          <Help />
        </div>
      </div>
    </PageLayout>
  );
};

export default Launchpad;
